import React, { useEffect, useState } from 'react';
import MetricsTable from '@/pages/intelligence/pages/TopCharts/PCGames/MetricsTable';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { steamSalesListForSteamSalesPage } from '@/pages/intelligence/pages/TopCharts/_config/metricOptions';
import { TopchartPageId } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import moment from 'moment';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import {
  commonPCConsoleDefaultFilterValue,
  handleMetricTableFilterParamsToApi,
} from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import { defaultMetrictableApiParams } from '@intelligence/pages/TopCharts/PCGames/MetricsTable/utils/constant';
import { useIntl, history } from 'umi';
import { getTopPCMetricTableCustomValidUrlParams, topchartSteamSalesValidCfg, hasShortLinkRatio } from '@intelligence/_config/urlPageValidCfg';
import { getOriginalShortLinkFilter, URL_FILTER_KEY, URL_CHART_METRIC, URL_CHART_TYPE } from '@intelligence/utils/urlPathCfg';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

export default function SteamSales() {
  const { formatMessage } = useIntl(); // 中英切换方法
  const entityType = 'pc';

  const filterLoading = false;
  const regionOptions: any[] = [];

  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.pcSteamSales?.type,
  });

  const defaultFilterValue = {
    ...commonPCConsoleDefaultFilterValue,
    [filterKeyMap.RANGE_TIME]: {
      type: 'date',
      value: ['', ''],
    },
  };

  const hasUrlShortLink = hasShortLinkRatio.topPCSteamSales;
  const [shortLinkLoading, setShortLinkLoading] = useState(hasUrlShortLink);
  const [urlCustomParams, setUrlCustomParams] = useState({});
  const [shortLinkValidQuery, setShortLinkValidQuery] = useState({
    [URL_CHART_TYPE]: topchartSteamSalesValidCfg?.[URL_CHART_TYPE]?.[0],
    [URL_CHART_METRIC]: topchartSteamSalesValidCfg?.[URL_CHART_METRIC]?.[0],
  });
  const [shortLinkFilterValue, setShortLinkFilterValue] = useState(null);

  const initPage = async () => {
    setShortLinkLoading(true);
    // 在url上取token，有则请求接口， 判断异常，返回函数值和url上的其他配置参数
    const { [URL_FILTER_KEY]: urlFilter } = await getOriginalShortLinkFilter();
    if (urlFilter) {
      // 有的筛选器的token时才用后面的参数，没有则不用
      const { params, validQuery } = getTopPCMetricTableCustomValidUrlParams(
        history,
        topchartSteamSalesValidCfg,
        steamSalesListForSteamSalesPage,
      );
      setUrlCustomParams({ ...params });
      setShortLinkValidQuery({ ...shortLinkValidQuery, ...validQuery });
      setShortLinkFilterValue(urlFilter);
    }
    setShortLinkLoading(false);
  };

  useEffect(() => {
    buriedPointPage(dbiPageLogMes.topChartsPCGamesSteamSales);
    // 进入页面时判断是否有tokenQuery等，有则进入
    initPage();
  }, []);

  const filterCfg: TopchartFilterCfgType = {
    filterKeyList: [
      filterKeyMap.MY_FILTER,
      filterKeyMap.RANGE_TIME,
      filterKeyMap.GENRETOR,
      filterKeyMap.ALL_Games,
      filterKeyMap.PREMIUM_RANGE,
      filterKeyMap.PRICE_RANGE,
      filterKeyMap.ALL_COMPANYS,
      filterKeyMap.RELEASE_TIME,
    ],
    companyFilterParams: {
      entity_type: entityType,
      source: 'vginsights',
      metric: 'est_revenue',
      region_type: 'market',
      regions: ['global'],
      granularity: 'monthly',
      start_date: '2023-12-27',
      end_date: '2024-01-25',
      data_sources: ['vginsights'],
      page_size: 500,
      search_name: '',
      page: 1,
      source_metric: {
        vginsights: ['est_revenue'],
      },
    },
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
      [filterKeyMap.RANGE_TIME]: {
        type: 'date',
        value: [moment().subtract(30, 'd'), moment().subtract(1, 'd')],
      },
    },
    specialParams: {
      regionOptions, // 国家筛选器options,
      defalutMarket: ['global'], // 没取到交集用的默认值
      disableNoChina: true,
      rangeTimeType: FilterTimeType.commonRange,
      disableIegg: false,
      disableAnnie: true,
      [filterKeyMap.ALL_Games]: {
        params: { entity_type: entityType, game_type: 'current_game' },
      },
      entityType
    },
    iconText: (
      <div>
        <p>{formatMessage({ id: 'dbi_releasedatefilter_info' })}</p>
      </div>
    ),
    buttonText: formatMessage({ id: 'dbi_steam_only' }),
    showConfirmButton: true,
    haveFilterSaveBtn: true,
    // 仅为适配 handleMetricTableFilterParamsToApi 的函数签名（不改变筛选行为）
    handleFilterToApiParams: (params: any) =>
      handleMetricTableFilterParamsToApi(params, entityType, {
        allSteamTagLength: 0,
        allGameModeLength: 0,
        allGameEngineLength: 0,
      }),
    hasUrlShortLink,
  };

  const defaultParams = {
    ...defaultMetrictableApiParams,
    entity_type: entityType,
    order_metric: 'alinea__est_revenue',
    order_source: 'alinea',
    source_metrics: {
      alinea: [
        'alinea__est_revenue',
        'alinea__est_units',
        'alinea__est_price_time',
        'alinea__est_price',
        'alinea__weighted_price',
        'alinea__playtime',
        'alinea__steamfollowers',
        'alinea__players_by_country',
      ],
      vginsights: [
        'est_revenue',
        'est_units',
        'est_price_time',
        'est_price',
        'weighted_price',
        'playtime',
        'followers',
        'players_by_country',
      ],
      // SteamSales 柱状图 Segment 切换到 Gamalytic 指标，需要确保后端也返回对应字段
      gamalytic: [
        'gamalytic__est_revenue',
        'gamalytic__est_units',
        'gamalytic__est_price_time',
        'gamalytic__est_price',
        'gamalytic__weighted_price',
        'gamalytic__playtime',
        'gamalytic__steamfollowers',
        'gamalytic__players_by_country',
      ],
    },
    topchart_name: 'Steam Sales',
    region_type: 'market',
    regions: ['global'],
    date_type: 'daily',
  };

  return (
    <>
      <MarqueeBanner />
      <MetricsTable
      entityType={entityType}
      pageId={TopchartPageId.SteamSales}
      pageLogInfo={dbiPageLogMes.topChartsPCGamesSteamSales}
      tableCloumsList={steamSalesListForSteamSalesPage}
      hasRegionFilter={false}
      cardMetricName={formatMessage({ id: 'dbi_steam_sales' })}
      filterCfg={filterCfg}
      filterLoading={filterLoading || shortLinkLoading}
      rangeTimeComponentDefault={latestTimeTypeHasData}
      defaultParams={defaultParams}
      urlCustomParams={urlCustomParams}
      shortLinkValidQuery={shortLinkValidQuery}
      shortLinkFilterValue={shortLinkFilterValue}
    />
    </>
  );
}
